import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { collection, getDocs } from "firebase/firestore";
import * as XLSX from "xlsx";
import db from "./Firebase";

const parseDate = (val) => {
    if (!val) return null;
    if (typeof val?.toDate === "function") return val.toDate();
    if (val instanceof Date) return val;
    if (typeof val?.seconds === "number") return new Date(val.seconds * 1000);
    const d = new Date(val);
    return isNaN(d) ? null : d;
};

const fmtDate = (val) => {
    const d = parseDate(val);
    if (!d) return "-";
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const fmtDateTime = (val) => {
    const d = parseDate(val);
    if (!d) return "-";
    return d.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

const compactPhone = (val) => String(val || "").replace(/\D/g, "").slice(-10);
const cleanText = (val, fb = "-") => (val === null || val === undefined || val === "" ? fb : String(val));

const JAN_2025 = new Date("2025-01-01T00:00:00");

export default function ShikshaMembershipUsers() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [logModal, setLogModal] = useState(null);
    const [workshopModal, setWorkshopModal] = useState(null);
    const [filterStatus, setFilterStatus] = useState("all");
    const [activeTab, setActiveTab] = useState("main");

    useEffect(() => {
        const fetchAll = async () => {
            setLoading(true);
            try {
                const [snap, glowSnap, wsSnap] = await Promise.all([
                    getDocs(collection(db, "nv_shiksha_subscriptions")),
                    getDocs(collection(db, "nv_glow_membership")),
                    getDocs(collection(db, "workshop_registration")),
                ]);

                // Build workshop counts + detail per phone (Jan 2025+)
                const phoneCounts = {};
                const phoneWorkshops = {};
                wsSnap.forEach(docSnap => {
                    const d = docSnap.data();
                    const dt = parseDate(d.created_date);
                    if (!dt || dt < JAN_2025) return;
                    const phone = compactPhone(d.phone);
                    if (!phone || phone.length < 10) return;
                    phoneCounts[phone] = (phoneCounts[phone] || 0) + 1;
                    if (!phoneWorkshops[phone]) phoneWorkshops[phone] = [];
                    phoneWorkshops[phone].push({
                        name: d.workshop_event_name || "-",
                        desc: d.description || "-",
                        date: d.workshop_Date || d.created_date,
                        location: d.workshop_location || "-",
                        payment: d.payment || "-",
                    });
                });

                // phone → price from nv_glow_membership
                const glowPriceMap = {};
                glowSnap.forEach(docSnap => {
                    const d = docSnap.data();
                    const phone = compactPhone(d.phone_no || docSnap.id);
                    if (phone) glowPriceMap[phone] = cleanText(d.price);
                });

                const data = [];
                snap.forEach(docSnap => {
                    const d = docSnap.data();
                    const phone = compactPhone(d.phone_no);
                    data.push({
                        id: docSnap.id,
                        phone,
                        name: cleanText(d.name),
                        membershipType: cleanText(d.membership_type),
                        price: d.price ?? "-",
                        startDate: d.start_date || null,
                        endDate: d.end_date || null,
                        createdDate: d.created_date || null,
                        status: cleanText(d.status),
                        tag: cleanText(d.tag),
                        subscriptionLog: Array.isArray(d.subscription_log) ? d.subscription_log : [],
                        earlierPlan: (() => {
                            const cd = parseDate(d.created_date);
                            if (cd && cd > new Date("2026-09-11T23:59:59")) return "New Enrollment";
                            return glowPriceMap[phone] || "-";
                        })(),
                        workshopCount: phoneCounts[phone] || 0,
                        workshops: (phoneWorkshops[phone] || []).sort((a, b) =>
                            (parseDate(b.date)?.getTime() || 0) - (parseDate(a.date)?.getTime() || 0)
                        ),
                    });
                });
                data.sort((a, b) => (parseDate(b.createdDate)?.getTime() || 0) - (parseDate(a.createdDate)?.getTime() || 0));
                setRows(data);
            } catch (err) {
                console.error("ShikshaMembershipUsers fetch error:", err);
            } finally {
                setLoading(false);
            }
        };
        fetchAll();
    }, []);

    const today = new Date();

    const baseFiltered = rows.filter(r => {
        const q = search.trim().toLowerCase();
        const matchSearch = !q ||
            r.phone.includes(q) ||
            r.name.toLowerCase().includes(q) ||
            r.membershipType.toLowerCase().includes(q) ||
            r.status.toLowerCase().includes(q);

        const endDate = parseDate(r.endDate);
        const isActive = endDate && endDate > today;
        const matchStatus =
            filterStatus === "all" ||
            (filterStatus === "active" && isActive) ||
            (filterStatus === "expired" && !isActive);

        return matchSearch && matchStatus;
    });

    const mainFiltered = baseFiltered.filter(r => r.earlierPlan !== "₹0 for 1 Week");
    const freeFiltered = baseFiltered.filter(r => r.earlierPlan === "₹0 for 1 Week");
    const filtered = activeTab === "main" ? mainFiltered : freeFiltered;

    const handleExport = () => {
        const sheetData = filtered.map((r, i) => ({
            "#": i + 1,
            "Name": r.name,
            "Phone No": r.phone,
            "Membership Type": r.membershipType,
            "Price": r.price,
            "Start Date": fmtDate(r.startDate),
            "End Date": fmtDate(r.endDate),
            "Status": r.status,
            "Tag": r.tag,
            "Earlier Plan": r.earlierPlan,
            "Created Date": fmtDate(r.createdDate),
        }));

        // Tab 2 — subscription log details
        const logData = [];
        filtered.forEach(r => {
            r.subscriptionLog.forEach((entry, i) => {
                logData.push({
                    "Phone No": r.phone,
                    "Name": r.name,
                    "Log #": i + 1,
                    "Membership Type": cleanText(entry.membership_type),
                    "Price": entry.price ?? "-",
                    "Tag": cleanText(entry.tag),
                    "Created Date": fmtDate(entry.created_date),
                });
            });
        });

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(sheetData), "Shiksha Members");
        if (logData.length > 0) {
            XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(logData), "Subscription Log");
        }
        XLSX.writeFile(wb, "shiksha_membership_users.xlsx");
    };

    const activeCount = rows.filter(r => { const d = parseDate(r.endDate); return d && d > today; }).length;
    const expiredCount = rows.length - activeCount;

    return (
        <>
            <div className="container-fluid py-4">

                {/* HEADER */}
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <div>
                        <h3 className="mb-1">Shiksha Membership Users</h3>
                        <div className="text-muted" style={{ fontSize: "13px" }}>
                            nv_shiksha_subscriptions · {rows.length} total records
                        </div>
                    </div>
                    <Link to="/" className="btn btn-outline-secondary btn-sm">Back</Link>
                </div>


                {/* SEARCH + FILTER + EXPORT */}
                <div className="card mb-3">
                    <div className="card-body py-3 px-4">
                        <div className="d-flex gap-3 align-items-center flex-wrap">
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Search by name, phone, type or status…"
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                style={{ maxWidth: "300px" }}
                            />
                            <select
                                className="form-select"
                                value={filterStatus}
                                onChange={e => setFilterStatus(e.target.value)}
                                style={{ maxWidth: "160px" }}
                            >
                                <option value="all">All</option>
                                <option value="active">Active Only</option>
                                <option value="expired">Expired Only</option>
                            </select>
                            {(search || filterStatus !== "all") && (
                                <span className="text-muted" style={{ fontSize: "13px" }}>
                                    {filtered.length} result{filtered.length !== 1 ? "s" : ""}
                                </span>
                            )}
                            <button
                                className="btn btn-success ms-auto"
                                onClick={handleExport}
                                disabled={loading || filtered.length === 0}
                                style={{ minWidth: "140px", fontWeight: 700 }}
                            >
                                Export Excel
                            </button>
                        </div>
                    </div>
                </div>

                {/* TABS */}
                {!loading && (
                    <ul className="nav nav-tabs mb-3">
                        <li className="nav-item">
                            <button
                                className={`nav-link ${activeTab === "main" ? "active fw-bold" : ""}`}
                                onClick={() => setActiveTab("main")}
                                style={activeTab !== "main" ? { color: "#fff" } : {}}
                            >
                                All Plans
                                <span className="badge bg-secondary ms-2" style={{ fontSize: "11px" }}>
                                    {mainFiltered.length}
                                </span>
                            </button>
                        </li>
                        <li className="nav-item">
                            <button
                                className={`nav-link ${activeTab === "free" ? "active fw-bold" : ""}`}
                                onClick={() => setActiveTab("free")}
                                style={activeTab !== "free" ? { color: "#fff" } : {}}
                            >
                                ₹0 for 1 Week
                                <span className="badge bg-warning text-dark ms-2" style={{ fontSize: "11px" }}>
                                    {freeFiltered.length}
                                </span>
                            </button>
                        </li>
                    </ul>
                )}

                {/* TABLE */}
                {loading ? (
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status" />
                        <div className="mt-2 text-muted" style={{ fontSize: "14px" }}>Loading…</div>
                    </div>
                ) : (
                    <div className="card">
                        <div className="table-responsive">
                            <table className="table table-bordered table-hover align-middle mb-0" style={{ fontSize: "13px" }}>
                                <thead className="table-dark">
                                    <tr>
                                        <th>#</th>
                                        <th>Name</th>
                                        <th>Phone No</th>
                                        <th>Membership Type</th>
                                        <th className="text-end">Price</th>
                                        <th>Start Date</th>
                                        <th>End Date</th>
                                        <th className="text-center">Status</th>
                                        <th>Earlier Plan</th>
                                        <th>Created Date</th>
                                        <th className="text-center">Workshops (Jan 2025+)</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filtered.length === 0 ? (
                                        <tr>
                                            <td colSpan="11" className="text-center py-4 text-muted">
                                                {search || filterStatus !== "all" ? "No results found" : "No data found"}
                                            </td>
                                        </tr>
                                    ) : filtered.map((row, i) => {
                                        const endDate = parseDate(row.endDate);
                                        const isActive = endDate && endDate > today;
                                        return (
                                            <tr key={row.id}>
                                                <td>{i + 1}</td>
                                                <td style={{ fontWeight: 600 }}>{row.name}</td>
                                                <td>{row.phone}</td>
                                                <td>
                                                    <span className="badge bg-info text-dark" style={{ fontSize: "10px", padding: "3px 6px", fontWeight: 600 }}>
                                                        {row.membershipType}
                                                    </span>
                                                </td>
                                                <td className="text-end fw-bold" style={{ color: "#16a34a" }}>
                                                    {row.price !== "-" ? `₹${row.price}` : "-"}
                                                </td>
                                                <td style={{ whiteSpace: "nowrap" }}>{fmtDate(row.startDate)}</td>
                                                <td style={{ whiteSpace: "nowrap" }}>
                                                    <span className={`badge ${isActive ? "bg-success" : "bg-danger"}`}
                                                        style={{ fontSize: "12px", padding: "5px 10px" }}>
                                                        {fmtDate(row.endDate)}
                                                    </span>
                                                </td>
                                                <td className="text-center">
                                                    <span className={`badge ${isActive ? "bg-success" : "bg-secondary"}`}
                                                        style={{ fontSize: "12px", padding: "5px 10px" }}>
                                                        {isActive ? "Active" : "Expired"}
                                                    </span>
                                                </td>
                                                {/* <td className="text-center">
                                                    {row.subscriptionLog.length > 0 ? (
                                                        <span
                                                            className="badge bg-primary"
                                                            style={{ cursor: "pointer", fontSize: "12px", padding: "5px 10px" }}
                                                            onClick={() => setLogModal(row)}
                                                            title="Click to view log"
                                                        >
                                                            {row.subscriptionLog.length} entr{row.subscriptionLog.length > 1 ? "ies" : "y"}
                                                        </span>
                                                    ) : "-"}
                                                </td> */}
                                                <td style={{ whiteSpace: "nowrap", color: "#7c3aed", fontWeight: 600 }}>
                                                    {row.earlierPlan !== "-" ? row.earlierPlan : <span className="text-muted fw-normal">-</span>}
                                                </td>
                                                <td style={{ whiteSpace: "nowrap" }}>{fmtDate(row.createdDate)}</td>
                                                <td className="text-center">
                                                    {row.workshopCount > 0 ? (
                                                        <span
                                                            className="badge bg-primary"
                                                            style={{ cursor: "pointer", fontSize: "13px", padding: "5px 10px" }}
                                                            onClick={() => setWorkshopModal(row)}
                                                            title="Click to see workshops"
                                                        >
                                                            {row.workshopCount}
                                                        </span>
                                                    ) : (
                                                        <span className="text-muted">0</span>
                                                    )}
                                                </td>

                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>

            {/* WORKSHOP DETAIL MODAL */}
            {workshopModal && (
                <div
                    onClick={() => setWorkshopModal(null)}
                    style={{
                        position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)",
                        zIndex: 9999, display: "flex", alignItems: "center",
                        justifyContent: "center", padding: "20px",
                    }}
                >
                    <div
                        onClick={e => e.stopPropagation()}
                        style={{
                            background: "#fff", borderRadius: "8px", width: "100%",
                            maxWidth: "600px", maxHeight: "80vh",
                            display: "flex", flexDirection: "column",
                            boxShadow: "0 8px 40px rgba(0,0,0,0.25)",
                        }}
                    >
                        <div className="d-flex justify-content-between align-items-center px-4 py-3 border-bottom">
                            <div>
                                <div className="fw-bold" style={{ fontSize: "15px" }}>
                                    Workshops Attended ({workshopModal.workshopCount})
                                </div>
                                <div className="text-muted" style={{ fontSize: "12px" }}>
                                    {workshopModal.name} · {workshopModal.phone}
                                </div>
                            </div>
                            <button className="btn-close" onClick={() => setWorkshopModal(null)} />
                        </div>
                        <div style={{ overflowY: "auto", padding: "16px" }}>
                            <table className="table table-sm table-bordered align-middle mb-0" style={{ fontSize: "13px" }}>
                                <thead className="table-dark">
                                    <tr><th>#</th><th>Workshop</th><th>Description</th><th>Workshop Date</th><th>Location</th><th>Payment</th></tr>
                                </thead>
                                <tbody>
                                    {workshopModal.workshops.map((w, i) => (
                                        <tr key={i}>
                                            <td>{i + 1}</td>
                                            <td>{w.name}</td>
                                            <td>{w.desc}</td>
                                            <td>{fmtDate(w.date)}</td>
                                            <td>{w.location}</td>
                                            <td>{w.payment}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {/* SUBSCRIPTION LOG MODAL */}
            {logModal && (
                <div
                    onClick={() => setLogModal(null)}
                    style={{
                        position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)",
                        zIndex: 9999, display: "flex", alignItems: "center",
                        justifyContent: "center", padding: "20px",
                    }}
                >
                    <div
                        onClick={e => e.stopPropagation()}
                        style={{
                            background: "#fff", borderRadius: "8px", width: "100%",
                            maxWidth: "600px", maxHeight: "80vh",
                            display: "flex", flexDirection: "column",
                            boxShadow: "0 8px 40px rgba(0,0,0,0.25)",
                        }}
                    >
                        <div className="d-flex justify-content-between align-items-center px-4 py-3 border-bottom">
                            <div>
                                <div className="fw-bold" style={{ fontSize: "15px" }}>
                                    Subscription Log ({logModal.subscriptionLog.length})
                                </div>
                                <div className="text-muted" style={{ fontSize: "12px" }}>
                                    {logModal.name} · {logModal.phone}
                                </div>
                            </div>
                            <button className="btn-close" onClick={() => setLogModal(null)} />
                        </div>
                        <div style={{ overflowY: "auto", padding: "16px" }}>
                            <table className="table table-sm table-bordered align-middle mb-0" style={{ fontSize: "13px" }}>
                                <thead className="table-dark">
                                    <tr>
                                        <th>#</th>
                                        <th>Membership Type</th>
                                        <th className="text-end">Price</th>
                                        <th>Tag</th>
                                        <th>Created Date</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {logModal.subscriptionLog.map((entry, i) => (
                                        <tr key={i}>
                                            <td>{i + 1}</td>
                                            <td>
                                                <span className="badge bg-info text-dark" style={{ fontSize: "12px" }}>
                                                    {cleanText(entry.membership_type)}
                                                </span>
                                            </td>
                                            <td className="text-end fw-bold">
                                                {entry.price !== undefined ? `₹${entry.price}` : "-"}
                                            </td>
                                            <td>{cleanText(entry.tag)}</td>
                                            <td style={{ whiteSpace: "nowrap" }}>{fmtDateTime(entry.created_date)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
